import Head from "next/head";
import PropTypes from "prop-types";
import { SITE } from "@/common/seo.config";
import { normalizeSchemaNode } from "@/lib/schema";

/**
 * SEO component for NivaLabs.ai
 * - Uses strong defaults aligned with seo.config / NivaLabs brand
 * - Accepts page-level overrides for title/description/og/twitter/schema
 * - Renders Organization + WebSite JSON-LD by default and merges page structuredData if provided
 */

const DEFAULTS = {
  siteName: SITE.siteName,
  siteUrl: SITE.siteUrl,
  logo: SITE.logo,
  defaultOgImage: SITE.defaultOgImage,
  twitterHandle: SITE.twitter,
  locale: SITE.locale,
  fbAppId: "",
  defaultTitle: "NivaLabs AI | AI Systems for Real Business Operations",
  defaultDescription:
    "Nivalabs helps enterprises ship production AI: agents, LLM apps, workflow automation, and integrations for operations-heavy teams worldwide.",
  defaultKeywords:
    "AI automation, enterprise AI, AI agents, LLM applications, workflow automation, machine learning, Nivalabs",
};

export const buildOrganizationSchema = ({
  siteUrl,
  siteName,
  logo,
  sameAs = [],
}) => ({
  "@type": "Organization",
  "@id": `${siteUrl}/#organization`,
  name: siteName,
  alternateName: ["Nivalabs", "Nivalabs AI", "NivaLabs"],
  url: siteUrl,
  logo: {
    "@type": "ImageObject",
    url: logo,
    contentUrl: logo,
  },
  image: logo,
  sameAs: Array.isArray(sameAs) ? sameAs : [],
  description:
    "Nivalabs builds production AI agents, custom LLM applications, workflow automation, and integrations for operational businesses.",
  email: "solutions@nivalabs.ai",
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "solutions@nivalabs.ai",
      telephone: "+91-98982-95005",
      areaServed: "Worldwide",
      availableLanguage: ["English"],
    },
  ],
  knowsAbout: [
    "AI agent development",
    "Custom LLM applications",
    "AI workflow automation",
    "Enterprise AI integrations",
    "AI MVP and proof of concept",
    "Operational AI systems",
  ],
  parentOrganization: {
    "@type": "Organization",
    name: "PySquad",
    url: "https://pysquad.com",
  },
});

const buildWebsiteSchema = ({ siteUrl, siteName }) => ({
  "@type": "WebSite",
  "@id": `${siteUrl}/#website`,
  url: siteUrl,
  name: siteName,
  description:
    "Production AI agents, LLM applications, workflow automation, and integrations for operations teams.",
  publisher: { "@id": `${siteUrl}/#organization` },
  inLanguage: "en-US",
});

const SEO = ({
  pageTitle,
  pageDescription,
  keywords,
  ogImage,
  ogTitle,
  ogUrl,
  canonical: canonicalProp,
  ogType = "website",
  locale = DEFAULTS.locale,
  noindex = false,
  structuredData = null,
  updatedTime = null,
  publishedTime = null,
  articleSection = null,
  children,
  titleTemplate = "%s | NivaLabs AI",
}) => {
  const title = pageTitle
    ? titleTemplate.replace("%s", pageTitle)
    : DEFAULTS.defaultTitle;
  const description = pageDescription ?? DEFAULTS.defaultDescription;
  const keywordString = keywords ?? DEFAULTS.defaultKeywords;
  const canonical = canonicalProp ?? ogUrl ?? DEFAULTS.siteUrl;
  const image = ogImage ?? DEFAULTS.defaultOgImage;
  const socialTitle = ogTitle ?? title;

  const orgSchema = buildOrganizationSchema({
    siteUrl: DEFAULTS.siteUrl,
    siteName: DEFAULTS.siteName,
    logo: DEFAULTS.logo,
    sameAs: SITE.sameAs,
  });

  const websiteSchema = buildWebsiteSchema({
    siteUrl: DEFAULTS.siteUrl,
    siteName: DEFAULTS.siteName,
  });

  const pageNodes = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [orgSchema, websiteSchema, ...pageNodes.map(normalizeSchemaNode)],
  };

  // Only emit a modified time we actually know. Falling back to "now" would
  // report a fresh edit on every request and devalue the signal.
  const modifiedTime = updatedTime ?? null;

  return (
    <Head>
      <meta charSet="utf-8" />
      <meta httpEquiv="x-ua-compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>{title}</title>
      <meta name="title" content={socialTitle} />
      <link rel="icon" href="/favicon/favicon.ico" sizes="any" />
      <link rel="icon" href="/favicon/favicon.svg" type="image/svg+xml" />
      <link
        rel="icon"
        type="image/png"
        sizes="96x96"
        href="/favicon/favicon-96x96.png"
      />
      <link rel="apple-touch-icon" href="/favicon/apple-touch-icon.png" />
      <link rel="manifest" href="/favicon/site.webmanifest" />
      <meta name="theme-color" content="#f7f8f8" />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <link
        rel="alternate"
        type="text/plain"
        title="llms.txt"
        href={`${DEFAULTS.siteUrl}/llms.txt`}
      />

      <meta name="description" content={description} />
      <meta name="keywords" content={keywordString} />
      <meta name="author" content={DEFAULTS.siteName} />
      <meta
        name="copyright"
        content={`${DEFAULTS.siteName} © ${new Date().getFullYear()}`}
      />
      <meta name="owner" content="nivalabs.ai" />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      {!noindex && (
        <>
          <meta
            name="googlebot"
            content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
          />
          <meta
            name="bingbot"
            content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
          />
        </>
      )}

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={socialTitle} />
      {image === DEFAULTS.defaultOgImage ? (
        <>
          <meta property="og:image:width" content={String(SITE.defaultOgImageWidth || 1200)} />
          <meta property="og:image:height" content={String(SITE.defaultOgImageHeight || 630)} />
        </>
      ) : null}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {DEFAULTS.fbAppId && (
        <meta property="fb:app_id" content={DEFAULTS.fbAppId} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={DEFAULTS.twitterHandle} />
      <meta name="twitter:site" content={DEFAULTS.twitterHandle} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={socialTitle} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd),
        }}
      />

      {children}
    </Head>
  );
};

SEO.propTypes = {
  pageTitle: PropTypes.string,
  pageDescription: PropTypes.string,
  keywords: PropTypes.string,
  ogImage: PropTypes.string,
  ogTitle: PropTypes.string,
  ogUrl: PropTypes.string,
  canonical: PropTypes.string,
  ogType: PropTypes.string,
  locale: PropTypes.string,
  noindex: PropTypes.bool,
  structuredData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  updatedTime: PropTypes.string,
  publishedTime: PropTypes.string,
  articleSection: PropTypes.string,
  children: PropTypes.node,
  titleTemplate: PropTypes.string,
};

export default SEO;
